// src/components/SettingsFab.js
import React from 'react';
import {
  Box,
  Drawer,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  IconButton,
  Button,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

const KEYS = {
  NO_AMBIENT: 'cp:ui:noAmbient',
  MOTION: 'cp:ui:motion',          // 'on' | 'off'
  FOCUS: 'cp:ui:focus',            // 'normal' | 'strong'
  MASCOT_HIDE: 'hide_mascot',      // '1' (hidden) | '0' (visible)
};

function safeDoc() {
  try { return typeof document !== 'undefined' ? document : null; } catch { return null; }
}

function applyNoAmbient(enabled) {
  const d = safeDoc(); if (!d) return;
  d.documentElement.classList.toggle('no-ambient', !!enabled);
}

function applyMotion(off) {
  const d = safeDoc(); if (!d) return;
  const de = d.documentElement;
  if (off) {
    de.setAttribute('data-motion', 'off');
    try { de.style.setProperty('--fx-motion-scale', '0'); } catch {}
  } else {
    de.removeAttribute('data-motion');
    try { de.style.setProperty('--fx-motion-scale', '1'); } catch {}
  }
}

function applyFocusStrong(enabled) {
  const d = safeDoc(); if (!d) return;
  const de = d.documentElement;
  if (enabled) de.setAttribute('data-focus', 'strong');
  else de.removeAttribute('data-focus');
}

function getInitialState() {
  let noAmbient = false, motionOff = false, focusStrong = false, showMascot = true;
  try {
    noAmbient = localStorage.getItem(KEYS.NO_AMBIENT) === '1';
    motionOff = (localStorage.getItem(KEYS.MOTION) || 'on') === 'off';
    focusStrong = (localStorage.getItem(KEYS.FOCUS) || 'normal') === 'strong';
    const hideMascot = localStorage.getItem(KEYS.MASCOT_HIDE) === '1';
    showMascot = !hideMascot;
  } catch {}
  applyNoAmbient(noAmbient);
  applyMotion(motionOff);
  applyFocusStrong(focusStrong);
  return { noAmbient, motionOff, focusStrong, showMascot };
}

export default function SettingsFab() {
  const [{ noAmbient, motionOff, focusStrong, showMascot }, setState] = React.useState(getInitialState);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const onKey = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key === ',') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Open/Toggle handlers from header/profile (no floating FAB needed)
  React.useEffect(() => {
    const onOpen = () => setOpen(true);
    const onToggle = () => setOpen((v) => !v);
    window.addEventListener('cp:ui:settings:open', onOpen);
    window.addEventListener('cp:ui:settings:toggle', onToggle);
    return () => {
      window.removeEventListener('cp:ui:settings:open', onOpen);
      window.removeEventListener('cp:ui:settings:toggle', onToggle);
    };
  }, []);

  const update = (next) => setState((s) => ({ ...s, ...next }));

  const onToggleAmbient = (e) => {
    const val = !!e.target.checked;
    update({ noAmbient: val });
    applyNoAmbient(val);
    try { localStorage.setItem(KEYS.NO_AMBIENT, val ? '1' : '0'); } catch {}
  };

  const onToggleMotion = (e) => {
    const off = !!e.target.checked;
    update({ motionOff: off });
    applyMotion(off);
    try { localStorage.setItem(KEYS.MOTION, off ? 'off' : 'on'); } catch {}
  };

  const onToggleFocus = (e) => {
    const strong = !!e.target.checked;
    update({ focusStrong: strong });
    applyFocusStrong(strong);
    try { localStorage.setItem(KEYS.FOCUS, strong ? 'strong' : 'normal'); } catch {}
  };

  const onToggleMascot = (e) => {
    const visible = !!e.target.checked;
    update({ showMascot: visible });
    try { localStorage.setItem(KEYS.MASCOT_HIDE, visible ? '0' : '1'); } catch {}
    try {
      window.dispatchEvent(new CustomEvent('cp:mascot:set', { detail: { visible } }));
    } catch {}
  };

  const onResetMascotPos = () => {
    try { window.dispatchEvent(new Event('cp:mascot:resetpos')); } catch {}
  };

  return (
    <>


      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        anchor="left"
        PaperProps={{
          sx: {
            width: { xs: 300, sm: 340 },
            background: 'var(--color-card)',
            borderRight: '1px solid var(--color-border)',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>Interface</Typography>
          <IconButton onClick={() => setOpen(false)} aria-label="Close settings" sx={{ color: 'var(--color-text)' }}>
            <CloseRoundedIcon />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: 'var(--color-border)' }} />

        <Box sx={{ p: 2, display: 'grid', gap: 1.5 }}>
          <FormControlLabel
            control={<Switch color="secondary" checked={noAmbient} onChange={onToggleAmbient} />}
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 800, color: 'var(--color-text)' }}>
                  Disable Ambient FX
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--color-subText)' }}>
                  Turn off background waves and sparkles for focus or battery savings.
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={<Switch color="secondary" checked={motionOff} onChange={onToggleMotion} />}
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 800, color: 'var(--color-text)' }}>
                  Reduce Motion
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--color-subText)' }}>
                  Disable animations and transitions across the app.
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={<Switch color="secondary" checked={focusStrong} onChange={onToggleFocus} />}
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 800, color: 'var(--color-text)' }}>
                  Strong Focus
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--color-subText)' }}>
                  Thicker, brighter focus rings for improved visibility.
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={<Switch color="secondary" checked={showMascot} onChange={onToggleMascot} />}
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 800, color: 'var(--color-text)' }}>
                  Show Mascot
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--color-subText)' }}>
                  Floating mascot that gently bobs and can be dragged, minimized, or hidden.
                </Typography>
              </Box>
            }
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: -0.5 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={onResetMascotPos}
              sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 800 }}
            >
              Reset Mascot Position
            </Button>
          </Box>
        </Box>

        <Divider sx={{ borderColor: 'var(--color-border)' }} />
        <Box sx={{ p: 2, pt: 1 }}>
          <Typography variant="caption" sx={{ color: 'var(--color-subText)' }}>
            Tip: Press Ctrl/Cmd + , to open this panel from anywhere.
          </Typography>
        </Box>
      </Drawer>
    </>
  );
}
